"use client";

import dayjs from "dayjs";
import React from "react";
import useMeasure from "react-use-measure";
import uPlot from "uplot";
import UplotReact from "uplot-react";

import { enNames, zhNames } from "@/utils/uplot-plugins/constant";

import { uplotTooltipPlugin } from "../../utils/uplot-plugins/tooltip-plugin";
import { ULineChartProps } from "./ULineChartProps";
import { drawSignalPlugin } from "./utils/draw-signal-plugin";
import { tooltipContent } from "./utils/tooltip-content";

const ULineChart = ({
  data,
  height = 400,
  dataInfo = [
    {
      strokeColor: "#648FD0",
      strokeWidth: 1,
      label: "Value",
      valueFormatter: (v) => `${v as number}%`,
    },
  ],
  dateLabel = "Time",
  lang = "zh",
  showTooltip = false,
  showSignal = false,
  showLegend = "none",
  syncId,
}: ULineChartProps) => {
  const [ref, { width: fullWidth }] = useMeasure();
  if (!data.length) return <></>;

  return (
    <div className="h-full w-full" ref={ref}>
      <UplotReact
        data={data}
        options={{
          width: fullWidth,
          height,
          cursor: {
            y: false,
            sync: { key: syncId ?? "" },
          },
          fmtDate: (tpl) =>
            uPlot.fmtDate(tpl, lang === "en" ? enNames : zhNames),
          series: [
            {
              value: (_u, v) => (v ? dayjs.unix(v).format("YYYY/MM/DD") : "-"),
              label: dateLabel,
            },
            ...dataInfo.map(
              (info) =>
                ({
                  label: info.label,
                  stroke: info.strokeColor,
                  width: info.strokeWidth,
                  value: (_u, value) => {
                    const formatter = info.valueFormatter ?? ((val) => val);
                    return value != undefined ? formatter(value) : "-";
                  },
                  points: { show: false },
                } as uPlot.Series)
            ),
          ],
          axes: [
            {
              grid: { show: false },
              ticks: { show: false },
              gap: 12,
            },
            {
              grid: { show: true },
              ticks: { show: false },
              values: (_u, value) => {
                const formatter = dataInfo[0].valueFormatter ?? ((val) => val);
                return value != undefined
                  ? value.map((v) => formatter(v))
                  : "-";
              },
            } as uPlot.Axis,
          ],
          legend: {
            show: showLegend !== "none",
            live: showLegend === "live",
            markers: {
              fill: (_u, idx) => {
                return dataInfo[idx - 1]?.strokeColor;
              },
            },
          },
          plugins: [
            ...(showTooltip
              ? [uplotTooltipPlugin(tooltipContent({ dataInfo }))]
              : []),
            ...(showSignal ? [drawSignalPlugin()] : []),
          ],
        }}
      />
    </div>
  );
};

export default React.memo(ULineChart);
